import { createSelector } from "@reduxjs/toolkit";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Events } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import {
	FormActivity,
	FormEngineActions,
	FormEngineSelectors
} from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import { DefaultWidgetMap } from "@com.mgmtp.a12.formengine/formengine-core/lib/view";
import { ButtonProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/button/main/button.api";
import { HiddenText } from "@com.mgmtp.a12.widgets/widgets-core/lib/common/main/hidden-text/hidden-text.view";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon/main/icon.view";
import { SizeContext } from "@com.mgmtp.a12.widgets/widgets-core/lib/layout/size-detector";
import { QuickAccessButton } from "@com.mgmtp.a12.widgets/widgets-core/lib/quick-access-button";
import { LocalizerContext } from "@com.mgmtp.a12.utils/utils-localization-react/lib/main";

import { setA11yDomId } from "../../../app/slices/accessibility/reducers";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { RESOURCE_KEYS } from "../../../localization";
import { DisplayType } from "../../../utilities/AnnotationHelper";
import { assertNonNullable } from "../../../utilities/assert";
import {
	CHANGE_ASSIGNMENT_EVENT,
	CUSTOM_SAVE,
	CUSTOM_SAVE_AND_CLOSE,
	SEND_MAIL_BUND_ID_EVENT,
	SEND_MAIL_EVENT,
	SEND_MAIL_INFORMATAION_REQUEST_EVENT,
	SHOW_CHANGELOG_EVENT,
	SHOW_FRISTEN_EVENT,
	SHOW_LINKS_EVENT,
	SHOW_WIEDERVORLAGEN_EVENT
} from "../../../utilities/variables/customEventNames";
import { AssignmentButton } from "../../assignment/components/AssignmentButton";
import { CHANGELOG_ACTIVITY } from "../../changelog/constants";
import { sendEmailAction } from "../../email/actions";
import { FRISTEN_ACTIVITY } from "../../fristen/constants";
import { LINKS_ACTIVITY } from "../../links/constants";
import { ANTRAG_DETAIL_ACTIVITY_ID } from "../../processOverview/store/actions";
import { WIEDERVORLAGEN_OVERVIEW_ACTIVITY } from "../../wiedervorlagen/constants";
import { isDocumentWithIdAndProcessMetadata } from "../../workflows/data/processMetadata";
import { StammdatenFormDescriptor } from "../../../utilities/descriptors";
import { EpayblActionButton, isEpayblActionEvent } from "../../zahlungsvorgangEpaybl/components/EpayblActionButton";

import { EventButtonContext } from "./contexts";
import { ToggleButton } from "./toggleButton";

export function getWiedervorlagenBadgeTooltipKey(count: number | undefined): string | undefined {
	if (!count) {
		return undefined;
	}

	return RESOURCE_KEYS.features.wiedervorlagen.dueCount[count > 1 ? "multiple" : "single"];
}

export function CustomButton(props: ButtonProps): React.ReactNode {
	const buttonRef = React.useRef<HTMLButtonElement | null>(null);
	const disabledDescriptionId = React.useId();
	const dispatch = useDispatch();
	const { localizer } = React.useContext(LocalizerContext);
	const eventButtonContext = React.useContext(EventButtonContext);
	const { eventName, a11yId } = eventButtonContext ?? {};

	React.useEffect(() => {
		if (a11yId && buttonRef.current) {
			dispatch(setA11yDomId([a11yId, buttonRef.current.id]));
		}
	}, [a11yId, dispatch]);

	/* Filters out add buttons in repeats, which have a "a12-add-button" ID and adds the "add-Icon" to them. */
	const icon = props.id?.includes("a12-add-button") && !props.icon ? <Icon>add</Icon> : props.icon;
	const title: string | undefined = props.title; // Only icon buttons have titles
	const ActionButton = isEpayblActionEvent(eventName) ? EpayblActionButton : DefaultWidgetMap.Button;

	const wiedervorlagenActivityOpen = useSelector(
		createSelector(ActivitySelectors.activities(), activities =>
			Object.keys(activities).includes(WIEDERVORLAGEN_OVERVIEW_ACTIVITY)
		)
	);
	const fristenActivityOpen = useSelector(
		createSelector(ActivitySelectors.activities(), activities => Object.keys(activities).includes(FRISTEN_ACTIVITY))
	);
	const linksActivityOpen = useSelector(
		createSelector(ActivitySelectors.activities(), activities => Object.keys(activities).includes(LINKS_ACTIVITY))
	);
	const changelogActivityOpen = useSelector(
		createSelector(ActivitySelectors.activities(), activities =>
			Object.keys(activities).includes(CHANGELOG_ACTIVITY)
		)
	);

	const activityId = React.useContext(ActivityIdContext) ?? "";
	const descriptor = useSelector(ActivitySelectors.activityPropById(activityId, it => it.descriptor));
	assertNonNullable(activityId);
	const data = useSelector(ActivitySelectors.data(activityId));

	const dueWiedervorlagenCount = useSelector(
		state => (ActivitySelectors.data(activityId)(state) as Record<string, any>)?.wiedervorlagenCount
	);

	const size = React.useContext(SizeContext).currentSize;
	const isSmall = size === "xs" || size === "sm";

	const antragUiState = useSelector(FormEngineSelectors.uiState(ANTRAG_DETAIL_ACTIVITY_ID));

	if (eventButtonContext?.display === DisplayType.HIDE) {
		return <DefaultWidgetMap.Button {...props} icon={icon} title={title} style={{ display: "none" }} />;
	}

	if (eventButtonContext?.display === DisplayType.DISABLE) {
		const disabledReason = eventButtonContext.disabledReason;
		return (
			<>
				<ActionButton
					{...props}
					icon={icon}
					title={title}
					disabled={true}
					buttonAttributes={
						disabledReason
							? {
									...props.buttonAttributes,
									"aria-describedby": [
										props.buttonAttributes?.["aria-describedby"],
										disabledDescriptionId
									]
										.filter(Boolean)
										.join(" ")
								}
							: props.buttonAttributes
					}
				/>
				{disabledReason && <HiddenText id={disabledDescriptionId}>{disabledReason}</HiddenText>}
			</>
		);
	}

	if (
		!antragUiState?.readonly &&
		!StammdatenFormDescriptor.isInstance(descriptor) &&
		["a12-view-button", "a12-return-button"].some(buttonId => props.id?.includes(buttonId))
	) {
		return null;
	}

	if (!eventButtonContext) {
		return <DefaultWidgetMap.Button {...props} icon={icon} title={title} />;
	}

	switch (eventName) {
		case CHANGE_ASSIGNMENT_EVENT:
			return !isSmall ? (
				FormActivity.Data.SingleDocumentData.isInstance(data) &&
					isDocumentWithIdAndProcessMetadata(data.document) && (
						<AssignmentButton
							readonly={!!props.disabled}
							activityId={activityId}
							document={data.document}
						></AssignmentButton>
					)
			) : (
				<DefaultWidgetMap.Button
					{...props}
					icon={icon}
					title={title}
					buttonRef={ref => {
						buttonRef.current = ref;
					}}
				/>
			);
		case SHOW_WIEDERVORLAGEN_EVENT:
			return (function WiedervorlagenToggle() {
				const tooltipKey = getWiedervorlagenBadgeTooltipKey(dueWiedervorlagenCount);
				const tooltip = tooltipKey
					? localizer({
							key: tooltipKey,
							args: {
								count: { type: "plain", value: dueWiedervorlagenCount }
							}
						})
					: undefined;
				return (
					<ToggleButton
						toggled={wiedervorlagenActivityOpen}
						badgeCount={dueWiedervorlagenCount}
						badgeTooltip={tooltip}
						{...props}
						title={title}
					/>
				);
			})();
		case SHOW_FRISTEN_EVENT:
			return (function FristenToggle() {
				return <ToggleButton toggled={fristenActivityOpen} {...props} title={title} />;
			})();
		case SHOW_LINKS_EVENT:
			return (function LinksToggle() {
				return <ToggleButton toggled={linksActivityOpen} {...props} title={title} />;
			})();
		case CUSTOM_SAVE:
		case CUSTOM_SAVE_AND_CLOSE:
			return <SaveAndCloseButton {...props} />;
		case SHOW_CHANGELOG_EVENT: {
			return (function ChangelogToggle() {
				return <ToggleButton toggled={changelogActivityOpen} {...props} title={title} />;
			})();
		}
		case SEND_MAIL_INFORMATAION_REQUEST_EVENT: {
			return (
				<DefaultWidgetMap.Button
					{...props}
					buttonAttributes={{ "aria-label": "E-Mail versenden (öffnet in externer Anwendung)", role: "link" }}
					icon={icon}
					title={title}
					buttonRef={ref => {
						buttonRef.current = ref;
					}}
				/>
			);
		}
		case SEND_MAIL_BUND_ID_EVENT:
		case SEND_MAIL_EVENT: {
			return <SendEmailButton {...props} />;
		}
		default:
			return (
				<ActionButton
					{...props}
					icon={icon}
					title={title}
					buttonRef={ref => {
						buttonRef.current = ref;
					}}
				/>
			);
	}
}

function SaveAndCloseButton(props: ButtonProps): React.ReactNode {
	const dispatch = useDispatch();
	const eventButtonContext = React.useContext(EventButtonContext);
	const { buttonPath } = eventButtonContext ?? {};

	const activityId = React.useContext(ActivityIdContext);
	assertNonNullable(activityId);

	const handleOnClick = React.useCallback(
		(action: string) =>
			dispatch(
				FormEngineActions.event({
					activityId: activityId,
					engineEvent: Events.eventButtonTriggered({
						buttonPath: buttonPath ?? [],
						name: action
					})
				})
			),
		[dispatch, activityId, buttonPath]
	);

	const actionItems = React.useMemo(() => {
		return [
			{
				text: "Speichern",
				graphic: <Icon>save</Icon>,
				onClick: () => handleOnClick(CUSTOM_SAVE)
			},
			{
				text: "Speichern und schließen",
				graphic: <Icon>save</Icon>,
				onClick: () => handleOnClick(CUSTOM_SAVE_AND_CLOSE)
			}
		];
	}, []);

	return (
		<QuickAccessButton secondary mainAction={<DefaultWidgetMap.Button {...props} />} actionItems={actionItems} />
	);
}

function SendEmailButton(props: ButtonProps): React.ReactNode {
	const dispatch = useDispatch();
	const { eventName, buttonPath } = React.useContext(EventButtonContext) ?? {};
	assertNonNullable(buttonPath);

	const activityId = React.useContext(ActivityIdContext);
	assertNonNullable(activityId);

	const onClickHandler = React.useCallback(
		() => dispatch(sendEmailAction({ activityId, buttonPath, bundId: eventName === SEND_MAIL_BUND_ID_EVENT })),
		[dispatch, activityId, buttonPath, eventName]
	);

	return <DefaultWidgetMap.Button {...props} onClick={onClickHandler} />;
}
